import type { EventCategory } from "@/types";

export interface Persona {
  id: string;
  label: string;
  icon: string;
  description: string;
  tagline: string;
  accentColor: string;
  terminology: {
    attendees: string;
    meeting: string;
    booking: string;
    client: string;
  };
  categories: EventCategory[];
  features: {
    smartLinks: boolean;
    analytics: boolean;
    teamView: boolean;
    nlpScheduling: boolean;
  };
}

// Define terminology and categories for each persona
const freelancerCategories: EventCategory[] = [
  {
    id: "client_call",
    label: "Client Call",
    icon: "📞",
    color: "#06b6d4",
    desc: "Direct client communication and check-ins",
    subcategories: [
      { id: "discovery", label: "Discovery", icon: "🔍", ids: [], defaultDur: 45, desc: "Initial client discovery call" },
      { id: "kickoff", label: "Project Kickoff", icon: "🚀", ids: [], defaultDur: 60, desc: "Project start meeting" },
      { id: "standup", label: "Status Standup", icon: "📊", ids: [], defaultDur: 30, desc: "Progress update call" },
      { id: "consultation", label: "Consultation", icon: "💡", ids: [], defaultDur: 50, desc: "Expert consultation session" },
      { id: "followup", label: "Follow-up", icon: "✉️", ids: [], defaultDur: 30, desc: "Post-project follow-up" },
    ],
  },
  {
    id: "sessions",
    label: "Sessions",
    icon: "🎯",
    color: "#0891b2",
    desc: "Work sessions and collaboration",
    subcategories: [
      { id: "work_session", label: "Work Session", icon: "💻", ids: [], defaultDur: 90, desc: "Deep work session" },
      { id: "review", label: "Deliverable Review", icon: "👁️", ids: [], defaultDur: 45, desc: "Review work output" },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: "⚙️",
    color: "#0d9488",
    desc: "Administrative and business tasks",
    subcategories: [
      { id: "proposal", label: "Proposal Meeting", icon: "📋", ids: [], defaultDur: 30, desc: "Discuss project proposal" },
      { id: "contract", label: "Contract Review", icon: "📄", ids: [], defaultDur: 30, desc: "Contract discussion" },
    ],
  },
];

const tutorCategories: EventCategory[] = [
  {
    id: "lessons",
    label: "Lessons",
    icon: "📚",
    color: "#8b5cf6",
    desc: "Direct teaching and instruction",
    subcategories: [
      { id: "private", label: "Private Lesson", icon: "👨‍🎓", ids: [], defaultDur: 50, desc: "One-on-one tutoring session" },
      { id: "group", label: "Group Class", icon: "👥", ids: [], defaultDur: 60, desc: "Group instruction class" },
      { id: "office_hours", label: "Office Hours", icon: "🕐", ids: [], defaultDur: 30, desc: "Open office hours" },
    ],
  },
  {
    id: "assessment",
    label: "Assessment",
    icon: "✅",
    color: "#a855f7",
    desc: "Testing and evaluation",
    subcategories: [
      { id: "exam", label: "Exam Session", icon: "🧪", ids: [], defaultDur: 60, desc: "Exam or test session" },
      { id: "progress", label: "Progress Check", icon: "📈", ids: [], defaultDur: 30, desc: "Student progress evaluation" },
    ],
  },
  {
    id: "parent_meeting",
    label: "Parent Meeting",
    icon: "👨‍👩‍👧",
    color: "#c084fc",
    desc: "Communication with parents/guardians",
    subcategories: [
      { id: "update", label: "Progress Update", icon: "💬", ids: [], defaultDur: 30, desc: "Parent-teacher conference" },
      { id: "planning", label: "Learning Plan", icon: "📝", ids: [], defaultDur: 45, desc: "Discuss learning objectives" },
    ],
  },
];

const creatorCategories: EventCategory[] = [
  {
    id: "collaborations",
    label: "Collaborations",
    icon: "🤝",
    color: "#ec4899",
    desc: "Partner and collaboration sessions",
    subcategories: [
      { id: "brand_deal", label: "Brand Deal", icon: "💼", ids: [], defaultDur: 45, desc: "Brand partnership discussion" },
      { id: "collab", label: "Collab Planning", icon: "🎬", ids: [], defaultDur: 60, desc: "Collaboration planning" },
      { id: "guest_appearance", label: "Guest Appearance", icon: "🎪", ids: [], defaultDur: 30, desc: "Guest appearance setup" },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: "📹",
    color: "#f472b6",
    desc: "Content creation and planning",
    subcategories: [
      { id: "planning", label: "Content Planning", icon: "📋", ids: [], defaultDur: 60, desc: "Plan content calendar" },
      { id: "podcast", label: "Podcast Recording", icon: "🎙️", ids: [], defaultDur: 90, desc: "Recording session" },
      { id: "editing", label: "Review & Edit", icon: "✂️", ids: [], defaultDur: 45, desc: "Content review session" },
    ],
  },
  {
    id: "community",
    label: "Community",
    icon: "👥",
    color: "#fb7185",
    desc: "Community and fan engagement",
    subcategories: [
      { id: "fan_meetup", label: "Fan Meetup", icon: "🎉", ids: [], defaultDur: 60, desc: "Meet with community/fans" },
      { id: "q_and_a", label: "Q&A Session", icon: "❓", ids: [], defaultDur: 45, desc: "Community Q&A session" },
    ],
  },
];

const businessCategories: EventCategory[] = [
  {
    id: "interview",
    label: "Interview",
    icon: "📋",
    color: "#7C3AED",
    desc: "Schedule candidate interviews with smart panel matching",
    subcategories: [
      { id: "technical", label: "Technical", icon: "💻", ids: ["3", "4", "5"], defaultDur: 60, desc: "Coding, system design, architecture" },
      { id: "culture", label: "Culture Fit", icon: "🤝", ids: ["1", "6"], defaultDur: 45, desc: "Values alignment, team fit" },
      { id: "leadership", label: "Leadership", icon: "👑", ids: ["1", "2"], defaultDur: 60, desc: "VP+ candidate evaluation" },
      { id: "product", label: "Product", icon: "📦", ids: ["8", "9"], defaultDur: 45, desc: "Product sense, strategy" },
      { id: "final", label: "Final Round", icon: "⭐", ids: ["1", "2", "3"], defaultDur: 60, desc: "Executive sign-off round" },
      { id: "screening", label: "Phone Screen", icon: "☎️", ids: ["7"], defaultDur: 30, desc: "Initial candidate screening" },
    ],
  },
  {
    id: "meeting",
    label: "Meeting",
    icon: "🤝",
    color: "#10b981",
    desc: "Team syncs, stand-ups, and collaborative sessions",
    subcategories: [
      { id: "one_on_one", label: "1-on-1", icon: "👥", ids: [], defaultDur: 30, desc: "Manager-report check-in" },
      { id: "touchpoint", label: "Touch Point", icon: "📌", ids: [], defaultDur: 15, desc: "Quick sync or status update" },
      { id: "standup", label: "Stand-up", icon: "⚡", ids: ["3", "4", "5"], defaultDur: 15, desc: "Daily agile stand-up" },
      { id: "brainstorm", label: "Brainstorm", icon: "💡", ids: [], defaultDur: 60, desc: "Ideation and creative session" },
      { id: "all_hands", label: "All Hands", icon: "🏢", ids: ["1", "2"], defaultDur: 60, desc: "Company-wide update" },
      { id: "sprint_plan", label: "Sprint Planning", icon: "🎯", ids: ["3", "4", "8", "9"], defaultDur: 60, desc: "Sprint kickoff and planning" },
    ],
  },
  {
    id: "review",
    label: "Review",
    icon: "🔍",
    color: "#f59e0b",
    desc: "Performance, code, and design review sessions",
    subcategories: [
      { id: "perf_review", label: "Performance", icon: "📈", ids: ["1", "2", "6"], defaultDur: 45, desc: "Quarterly/annual performance review" },
      { id: "code_review", label: "Code Review", icon: "💻", ids: ["3", "4", "5"], defaultDur: 30, desc: "Pull request or architecture review" },
      { id: "design_review", label: "Design Review", icon: "🎨", ids: ["8", "9", "3"], defaultDur: 45, desc: "UX/product design critique" },
      { id: "retro", label: "Retrospective", icon: "🔄", ids: ["3", "4", "5"], defaultDur: 60, desc: "Sprint or project retrospective" },
      { id: "360_feedback", label: "360 Feedback", icon: "💬", ids: ["6"], defaultDur: 30, desc: "Multi-rater feedback session" },
    ],
  },
  {
    id: "other",
    label: "Other",
    icon: "⚙️",
    color: "#8b5cf6",
    desc: "Client calls, training, demos, and custom events",
    subcategories: [
      { id: "client_call", label: "Client Call", icon: "☎️", ids: ["2", "8"], defaultDur: 30, desc: "External client meeting" },
      { id: "training", label: "Training", icon: "🎓", ids: ["6"], defaultDur: 60, desc: "Workshop or learning session" },
      { id: "onboarding", label: "Onboarding", icon: "🚀", ids: ["6", "7"], defaultDur: 45, desc: "New hire orientation" },
      { id: "demo", label: "Demo / Showcase", icon: "🎬", ids: ["8", "9", "3"], defaultDur: 30, desc: "Product or feature demo" },
      { id: "custom", label: "Custom", icon: "✏️", ids: [], defaultDur: 30, desc: "Build your own event" },
    ],
  },
];

const personalCategories: EventCategory[] = [
  {
    id: "social",
    label: "Social",
    icon: "🎉",
    color: "#10b981",
    desc: "Social gatherings and hangouts",
    subcategories: [
      { id: "coffee", label: "Coffee Chat", icon: "☕", ids: [], defaultDur: 30, desc: "Casual coffee meetup" },
      { id: "dinner", label: "Dinner", icon: "🍽️", ids: [], defaultDur: 90, desc: "Dinner with friends" },
      { id: "game_night", label: "Game Night", icon: "🎮", ids: [], defaultDur: 120, desc: "Game night hangout" },
      { id: "catchup", label: "Catch-up", icon: "💬", ids: [], defaultDur: 45, desc: "Casual catch-up chat" },
    ],
  },
  {
    id: "activities",
    label: "Activities",
    icon: "⚽",
    color: "#14b8a6",
    desc: "Sports and recreational activities",
    subcategories: [
      { id: "workout", label: "Workout", icon: "💪", ids: [], defaultDur: 60, desc: "Exercise session" },
      { id: "sports", label: "Sports Activity", icon: "⚽", ids: [], defaultDur: 90, desc: "Play sports with friends" },
      { id: "outdoor", label: "Outdoor Activity", icon: "🌳", ids: [], defaultDur: 120, desc: "Outdoor adventure" },
    ],
  },
  {
    id: "studying",
    label: "Studying",
    icon: "📖",
    color: "#06b6d4",
    desc: "Learning and study groups",
    subcategories: [
      { id: "study_group", label: "Study Group", icon: "📚", ids: [], defaultDur: 90, desc: "Group study session" },
      { id: "project", label: "Project Work", icon: "🛠️", ids: [], defaultDur: 120, desc: "Group project session" },
    ],
  },
];

const salesCategories: EventCategory[] = [
  {
    id: "pipeline",
    label: "Pipeline",
    icon: "🎯",
    color: "#f97316",
    desc: "Sales pipeline and deal progression",
    subcategories: [
      { id: "discovery_call", label: "Discovery Call", icon: "🔍", ids: [], defaultDur: 45, desc: "Initial discovery with prospect" },
      { id: "demo", label: "Demo", icon: "🎬", ids: [], defaultDur: 60, desc: "Product demonstration" },
      { id: "negotiation", label: "Negotiation", icon: "💼", ids: [], defaultDur: 50, desc: "Terms and pricing negotiation" },
      { id: "closing_call", label: "Closing Call", icon: "✅", ids: [], defaultDur: 30, desc: "Final closing call" },
    ],
  },
  {
    id: "followups",
    label: "Follow-ups",
    icon: "📧",
    color: "#fb923c",
    desc: "Client relationship management",
    subcategories: [
      { id: "checkin", label: "Check-in", icon: "👋", ids: [], defaultDur: 20, desc: "Quick check-in with prospect" },
      { id: "qbr", label: "QBR", icon: "📊", ids: [], defaultDur: 60, desc: "Quarterly business review" },
      { id: "renewal", label: "Renewal", icon: "🔄", ids: [], defaultDur: 45, desc: "Contract renewal discussion" },
    ],
  },
  {
    id: "internal",
    label: "Internal",
    icon: "👥",
    color: "#fdba74",
    desc: "Internal sales team meetings",
    subcategories: [
      { id: "team_standup", label: "Team Standup", icon: "⚡", ids: [], defaultDur: 15, desc: "Daily team standup" },
      { id: "forecast_review", label: "Forecast Review", icon: "📈", ids: [], defaultDur: 45, desc: "Sales forecast review" },
      { id: "pipeline_review", label: "Pipeline Review", icon: "🔄", ids: [], defaultDur: 60, desc: "Pipeline analysis meeting" },
    ],
  },
];

const healthcareCategories: EventCategory[] = [
  {
    id: "patient_care",
    label: "Patient Care",
    icon: "👨‍⚕️",
    color: "#14b8a6",
    desc: "Direct patient care and consultations",
    subcategories: [
      { id: "consultation", label: "Consultation", icon: "💬", ids: [], defaultDur: 45, desc: "Patient consultation appointment" },
      { id: "followup", label: "Follow-up", icon: "✅", ids: [], defaultDur: 30, desc: "Patient follow-up visit" },
      { id: "checkup", label: "Check-up", icon: "🩺", ids: [], defaultDur: 40, desc: "Routine check-up appointment" },
      { id: "emergency", label: "Emergency Slot", icon: "🚨", ids: [], defaultDur: 20, desc: "Emergency appointment slot" },
    ],
  },
  {
    id: "staff",
    label: "Staff",
    icon: "👔",
    color: "#0d9488",
    desc: "Staff coordination and meetings",
    subcategories: [
      { id: "rounds", label: "Rounds", icon: "🔄", ids: [], defaultDur: 120, desc: "Medical rounds" },
      { id: "handover", label: "Handover", icon: "🤝", ids: [], defaultDur: 30, desc: "Shift handover meeting" },
      { id: "team_huddle", label: "Team Huddle", icon: "💪", ids: [], defaultDur: 15, desc: "Quick team huddle" },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: "📋",
    color: "#06b6d4",
    desc: "Administrative and compliance",
    subcategories: [
      { id: "insurance_review", label: "Insurance Review", icon: "📄", ids: [], defaultDur: 30, desc: "Insurance verification" },
      { id: "scheduling_block", label: "Scheduling Block", icon: "🚫", ids: [], defaultDur: 0, desc: "Block on schedule" },
    ],
  },
];

const realestateCategories: EventCategory[] = [
  {
    id: "showings",
    label: "Showings",
    icon: "🏠",
    color: "#eab308",
    desc: "Property showings and tours",
    subcategories: [
      { id: "property_tour", label: "Property Tour", icon: "🔑", ids: [], defaultDur: 45, desc: "Single property showing" },
      { id: "open_house", label: "Open House", icon: "🎪", ids: [], defaultDur: 180, desc: "Open house event" },
      { id: "virtual_tour", label: "Virtual Tour", icon: "🖥️", ids: [], defaultDur: 30, desc: "Virtual property tour" },
    ],
  },
  {
    id: "transactions",
    label: "Transactions",
    icon: "📋",
    color: "#eab308",
    desc: "Deal-related meetings",
    subcategories: [
      { id: "offer_review", label: "Offer Review", icon: "💰", ids: [], defaultDur: 30, desc: "Review offer details" },
      { id: "closing", label: "Closing", icon: "✍️", ids: [], defaultDur: 60, desc: "Closing meeting" },
      { id: "inspection", label: "Inspection", icon: "🔍", ids: [], defaultDur: 120, desc: "Property inspection" },
    ],
  },
  {
    id: "client_relations",
    label: "Client Relations",
    icon: "🤝",
    color: "#facc15",
    desc: "Client consultations and meetings",
    subcategories: [
      { id: "buyer_consultation", label: "Buyer Consultation", icon: "👤", ids: [], defaultDur: 50, desc: "Buyer consultation" },
      { id: "seller_meeting", label: "Seller Meeting", icon: "👥", ids: [], defaultDur: 45, desc: "Seller consultation" },
      { id: "market_update", label: "Market Update", icon: "📊", ids: [], defaultDur: 30, desc: "Market analysis update" },
    ],
  },
];

const beautyCategories: EventCategory[] = [
  {
    id: "services",
    label: "Services",
    icon: "✨",
    color: "#d946ef",
    desc: "Client beauty and grooming services",
    subcategories: [
      { id: "haircut", label: "Haircut", icon: "✂️", ids: [], defaultDur: 45, desc: "Haircut and styling" },
      { id: "color", label: "Color & Highlights", icon: "🎨", ids: [], defaultDur: 120, desc: "Hair coloring service" },
      { id: "nails", label: "Nails", icon: "💅", ids: [], defaultDur: 60, desc: "Manicure or pedicure" },
      { id: "facial", label: "Facial", icon: "🧖", ids: [], defaultDur: 60, desc: "Facial treatment" },
      { id: "makeup", label: "Makeup", icon: "💄", ids: [], defaultDur: 90, desc: "Makeup session" },
      { id: "waxing", label: "Waxing", icon: "🌟", ids: [], defaultDur: 30, desc: "Waxing service" },
    ],
  },
  {
    id: "consultations",
    label: "Consultations",
    icon: "💬",
    color: "#e879f9",
    desc: "Client consultations and assessments",
    subcategories: [
      { id: "new_client", label: "New Client Consult", icon: "👋", ids: [], defaultDur: 30, desc: "First-time client consultation" },
      { id: "style_consult", label: "Style Consultation", icon: "🪞", ids: [], defaultDur: 45, desc: "Style and look planning" },
      { id: "bridal", label: "Bridal Consult", icon: "💍", ids: [], defaultDur: 60, desc: "Wedding beauty planning" },
    ],
  },
  {
    id: "wellness",
    label: "Wellness",
    icon: "🧘",
    color: "#c084fc",
    desc: "Wellness and self-care treatments",
    subcategories: [
      { id: "massage", label: "Massage", icon: "💆", ids: [], defaultDur: 60, desc: "Massage therapy session" },
      { id: "spa_package", label: "Spa Package", icon: "🛁", ids: [], defaultDur: 120, desc: "Full spa experience" },
      { id: "skincare", label: "Skincare Routine", icon: "🧴", ids: [], defaultDur: 45, desc: "Skincare consultation and treatment" },
    ],
  },
];

const fitnessCategories: EventCategory[] = [
  {
    id: "training",
    label: "Training",
    icon: "💪",
    color: "#ef4444",
    desc: "Training sessions",
    subcategories: [
      { id: "one_on_one", label: "1-on-1 Session", icon: "👤", ids: [], defaultDur: 60, desc: "Personal training session" },
      { id: "group_class", label: "Group Class", icon: "👥", ids: [], defaultDur: 60, desc: "Group fitness class" },
      { id: "hiit", label: "HIIT", icon: "⚡", ids: [], defaultDur: 45, desc: "High-intensity interval training" },
      { id: "yoga", label: "Yoga", icon: "🧘", ids: [], defaultDur: 60, desc: "Yoga session" },
    ],
  },
  {
    id: "assessment",
    label: "Assessment",
    icon: "📊",
    color: "#f87171",
    desc: "Fitness assessments and evaluations",
    subcategories: [
      { id: "initial_consultation", label: "Initial Consultation", icon: "🤝", ids: [], defaultDur: 45, desc: "Initial fitness assessment" },
      { id: "progress_check", label: "Progress Check", icon: "📈", ids: [], defaultDur: 30, desc: "Progress evaluation" },
      { id: "body_composition", label: "Body Composition", icon: "⚖️", ids: [], defaultDur: 20, desc: "Body composition analysis" },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: "⚙️",
    color: "#fca5a5",
    desc: "Administrative tasks",
    subcategories: [
      { id: "program_design", label: "Program Design", icon: "📋", ids: [], defaultDur: 60, desc: "Fitness program design" },
      { id: "nutrition_review", label: "Nutrition Review", icon: "🥗", ids: [], defaultDur: 45, desc: "Nutrition consultation" },
    ],
  },
];

export const PERSONAS: Persona[] = [
  {
    id: "freelancer",
    label: "Freelancer",
    icon: "🎯",
    description: "Perfect for independent professionals managing client work",
    tagline: "Schedule client sessions and project work",
    accentColor: "#06b6d4",
    terminology: {
      attendees: "clients",
      meeting: "session",
      booking: "session",
      client: "client",
    },
    categories: freelancerCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: false,
      nlpScheduling: true,
    },
  },
  {
    id: "tutor",
    label: "Tutor",
    icon: "📚",
    description: "For educators, instructors, and tutors",
    tagline: "Manage lessons, assessments, and student interactions",
    accentColor: "#8b5cf6",
    terminology: {
      attendees: "students",
      meeting: "lesson",
      booking: "lesson",
      client: "student",
    },
    categories: tutorCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: false,
      nlpScheduling: true,
    },
  },
  {
    id: "creator",
    label: "Creator",
    icon: "🎬",
    description: "For content creators, influencers, and artists",
    tagline: "Coordinate collaborations and content sessions",
    accentColor: "#ec4899",
    terminology: {
      attendees: "collaborators",
      meeting: "session",
      booking: "session",
      client: "collaborator",
    },
    categories: creatorCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: true,
      nlpScheduling: true,
    },
  },
  {
    id: "enterprise",
    label: "Enterprise",
    icon: "🏢",
    description: "For corporate teams and enterprise organizations",
    tagline: "Interviews, team syncs, and enterprise scheduling",
    accentColor: "#7C3AED",
    terminology: {
      attendees: "attendees",
      meeting: "meeting",
      booking: "booking",
      client: "participant",
    },
    categories: businessCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: true,
      nlpScheduling: true,
    },
  },
  {
    id: "personal",
    label: "Personal",
    icon: "👥",
    description: "For casual scheduling with friends and family",
    tagline: "Plan hangouts with friends and activities",
    accentColor: "#10b981",
    terminology: {
      attendees: "friends",
      meeting: "hangout",
      booking: "hangout",
      client: "friend",
    },
    categories: personalCategories,
    features: {
      smartLinks: false,
      analytics: false,
      teamView: false,
      nlpScheduling: false,
    },
  },
  {
    id: "sales",
    label: "Sales",
    icon: "💰",
    description: "Close deals faster with smart scheduling",
    tagline: "Pipeline meetings, demos, and client calls",
    accentColor: "#f97316",
    terminology: {
      attendees: "prospects",
      meeting: "call",
      booking: "appointment",
      client: "prospect",
    },
    categories: salesCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: true,
      nlpScheduling: true,
    },
  },
  {
    id: "healthcare",
    label: "Healthcare",
    icon: "🏥",
    description: "Streamline patient and staff scheduling",
    tagline: "Appointments, consultations, and rounds",
    accentColor: "#14b8a6",
    terminology: {
      attendees: "patients",
      meeting: "appointment",
      booking: "appointment",
      client: "patient",
    },
    categories: healthcareCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: true,
      nlpScheduling: true,
    },
  },
  {
    id: "realestate",
    label: "Real Estate",
    icon: "🏠",
    description: "Manage showings, closings, and client meetings",
    tagline: "Property tours, open houses, and closings",
    accentColor: "#eab308",
    terminology: {
      attendees: "clients",
      meeting: "showing",
      booking: "appointment",
      client: "buyer",
    },
    categories: realestateCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: false,
      nlpScheduling: true,
    },
  },
  {
    id: "beauty",
    label: "Beauty",
    icon: "💇",
    description: "For salons, spas, and beauty professionals",
    tagline: "Appointments, consultations, and self-care",
    accentColor: "#d946ef",
    terminology: {
      attendees: "clients",
      meeting: "appointment",
      booking: "appointment",
      client: "client",
    },
    categories: beautyCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: false,
      nlpScheduling: true,
    },
  },
  {
    id: "fitness",
    label: "Fitness",
    icon: "💪",
    description: "Personal training and class scheduling",
    tagline: "Train clients, run classes, track progress",
    accentColor: "#ef4444",
    terminology: {
      attendees: "clients",
      meeting: "session",
      booking: "session",
      client: "client",
    },
    categories: fitnessCategories,
    features: {
      smartLinks: true,
      analytics: true,
      teamView: false,
      nlpScheduling: true,
    },
  },
];

export function getPersonaById(id: string): Persona | undefined {
  return PERSONAS.find((p) => p.id === id);
}

export function getPersonaTerminology(personaId: string | null, key: keyof Persona["terminology"]): string {
  if (!personaId) {
    return key === "attendees" ? "attendees" : key === "meeting" ? "meeting" : key === "booking" ? "booking" : "participant";
  }
  const persona = getPersonaById(personaId);
  if (!persona) return key;
  return persona.terminology[key] || key;
}
